import type { SecretId, SecretListing } from "@repo/app-types/entities/secret";
import type {
  Tool,
  ToolPatchPayload,
  ToolPostPayload,
  ToolSettings,
} from "@repo/app-types/entities/tool";
import type { CodeToolRunReport } from "@repo/app-types/entities/tools/code";
import {
  buildEntityTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

/** A tool as its page shows it: the tool, and the secrets a code tool references. */
export interface ToolDetails {
  tool: Tool;
  secrets: SecretListing[];
}

const toolApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allTools: builder.query<Tool[], void, { data: { tools: Tool[] } }>({
      query: () => "/tools",
      transformResponse: (response) => response.data.tools,
      providesTags: (result) => getTagsProvidedByList(TagType.Tool, result),
    }),
    voiceAgentSigningKey: builder.query<
      string,
      void,
      { data: { signing_key: string } }
    >({
      query: () => "/tools/voice-agent-signing-key",
      transformResponse: (response) => response.data.signing_key,
    }),
    getTool: builder.query<ToolDetails, string, { data: ToolDetails }>({
      query: (toolId) => `/tools/${toolId}`,
      transformResponse: (response) => response.data,
      // A secret's edit refetches the tools that list it.
      providesTags: (result, error, toolId) => [
        ...getTagsProvidedByEntity(TagType.Tool, toolId),
        ...(result?.secrets ?? []).map((secret) =>
          buildEntityTag(TagType.Secret, secret),
        ),
      ],
    }),
    createTool: builder.mutation<
      Tool,
      ToolPostPayload,
      { data: { tool: Tool } }
    >({
      query: (payload) => ({
        url: "/tools",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => response.data.tool,
      // A tool created from a proposal applies it.
      invalidatesTags: (_result, _error, payload) => [
        ...getTagsInvalidatedByCreate(TagType.Tool),
        ...(payload.proposal_id
          ? getTagsInvalidatedByUpdate(
              TagType.CopilotProposal,
              payload.proposal_id,
            )
          : []),
      ],
    }),
    updateTool: builder.mutation<
      ToolDetails,
      { toolId: string; payload: ToolPatchPayload },
      { data: ToolDetails }
    >({
      query: ({ toolId, payload }) => ({
        url: `/tools/${toolId}`,
        method: "PATCH",
        body: payload,
      }),
      transformResponse: (response) => response.data,
      // The saved tool is in the cache before the form resets to it, so the
      // form doesn't show the old tool until the refetch lands.
      async onQueryStarted({ toolId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Synchronous, unlike an upsert, so it lands before the caller's
          // own continuation resets the form.
          dispatch(toolApi.util.updateQueryData("getTool", toolId, () => data));
        } catch {
          // The caller reports the failure.
        }
      },
      invalidatesTags: (result, error, { toolId }) =>
        getTagsInvalidatedByUpdate(TagType.Tool, toolId),
    }),
    testTool: builder.mutation<
      CodeToolRunReport,
      {
        toolId: string;
        /**
         * The script's `input` as the tester wrote it; the API checks it.
         */
        input: object;
        /**
         * Unsaved settings to run in place of the saved ones; the API checks
         * they are a code tool's.
         */
        settings?: ToolSettings;
        /** Unsaved secrets to run with in place of the referenced ones. */
        secret_ids?: SecretId[];
      },
      { data: CodeToolRunReport }
    >({
      query: ({ toolId, input, settings, secret_ids }) => ({
        url: `/tools/${toolId}/test`,
        method: "POST",
        body: { input, settings, secret_ids },
      }),
      transformResponse: (response) => response.data,
    }),
    deleteTool: builder.mutation<void, string>({
      query: (toolId) => ({
        url: `/tools/${toolId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, toolId) =>
        getTagsInvalidatedByDelete(TagType.Tool, toolId),
    }),
  }),
});

export const {
  useAllToolsQuery,
  useGetToolQuery,
  useCreateToolMutation,
  useUpdateToolMutation,
  useDeleteToolMutation,
  useTestToolMutation,
  useVoiceAgentSigningKeyQuery,
} = toolApi;
